import AutoSizer from 'react-virtualized-auto-sizer';
import CompareSlider from './CompareSlider.jsx';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ObservationsGrid from './ObservationsGrid.jsx';
import PropTypes, {array, bool, func, object, string} from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import SideBySide from './SideBySide.jsx';
import SideBySideIcon from '../assets/SideBySide.jsx';
import SlideIcon from '../assets/SlideCompare.jsx';
import ToggleButton from './ToggleButton.jsx';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  SvgIcon,
  Typography,
  styled,
} from '@mui/material';
import {COMPARE_METHODS} from '../constants/compare.js';
import {Drawer, MapParent, Modal, RightFrame} from './modal.js';
import {Map, TileLayer, VectorLayer, VectorTileLayer} from '@planet/layers';
import {SizerCallback} from './SizerCallback.js';
import {ThemeProvider} from '../theme-provider.jsx';
import {buffer, getWidth} from 'ol/extent';
import {fromExtent} from 'ol/geom/Polygon';
import {getDrawStyle} from '../util/styles.js';
import {getFeatureFromEvent, getMosaicsByDate} from '../util/map.js';
import {getImageryURL} from '../util/collection.js';
import {getRenderPixel} from 'ol/render';
import {toLocaleDateString} from '../util/date.js';
import {transformExtent} from 'ol/proj';
import {useAuth} from '@apps-common/auth-react';
import {usePopup} from '../hooks/popup.jsx';

const CardHeaderTitle = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const ChooseDateBox = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  width: '50%',
  top: 0,
  left: 0,
  height: '100%',
  backgroundColor: 'rgba(5,23,25,0.5)',
});

const RightCardBox = styled('div')({
  display: 'flex',
  width: '50%',
  position: 'absolute',
  left: '50%',
  top: 0,
  justifyContent: 'center',
});

const LeftCardBox = styled('div')({
  display: 'flex',
  width: '50%',
  position: 'absolute',
  left: 0,
  top: 0,
  justifyContent: 'center',
});

const ChooseDateTypography = styled(Typography)({
  maxWidth: 250,
  textAlign: 'center',
});

const BareStyledCard = styled(Card)(({theme}) => ({
  display: 'flex',
  height: 43,
  top: theme.spacing(2),
  position: 'absolute',
  maxWidth: 300,
  justifyContent: 'center',
}));

const StyledCard = ({children}) => (
  <ThemeProvider>
    <BareStyledCard>{children}</BareStyledCard>
  </ThemeProvider>
);

StyledCard.propTypes = {
  children: PropTypes.node,
};

const ToolbarButton = styled(Button)(({theme}) => ({
  margin: theme.spacing(1),
}));

/**
 * Main component for the change view modal. This wraps the map view with slider,
 * the side by side compare mode and the list of observations in the bottom with
 * the thumbnails.
 */
const ChangeViewer = ({
  bounds,
  detectionsURL,
  featureStyle,
  imageryURL,
  layer,
  observations,
  onClose,
  open,
  onSelectResult,
  resultInfo,
  mosaicsInfo,
}) => {
  const initial = 50;
  const mapRef = useRef();
  // using an effect is not lively enough for the slider values
  const sliderValueRef = useRef(50);
  const [view, setView] = useState(null);
  const [current, setCurrent] = useState(null);
  const [compare, setCompare] = useState(null);
  const [compareMethod, setCompareMethod] = useState(COMPARE_METHODS.SLIDER);
  const [extent, setExtent] = useState(null);
  const [hovering, updateHovering] = useState(false);
  const auth = useAuth();

  const onCompareMethodChange = useCallback(value => {
    setCompareMethod(value);
    setExtent(null);
  }, []);

  const interval = useMemo(() => {
    const dt1 = new Date(observations[0]?.observed);
    const dt2 = new Date(observations[1]?.observed);
    let diff = (dt2.getTime() - dt1.getTime()) / 1000;
    diff /= 60 * 60 * 24 * 7 * 4;
    return Math.abs(Math.round(diff));
  }, [observations]);

  const onPointerMoveSlider = useCallback(
    event => {
      const feature = getFeatureFromEvent(event);
      updateHovering(!!feature);
    },
    [updateHovering],
  );

  const popupCallback = useCallback((event, feature, extent) => {
    if (feature) {
      setExtent(buffer(extent, getWidth(extent) * 0.15));
    } else {
      setExtent(null);
    }
  }, []);

  const [onMapClick] = usePopup(
    popupCallback,
    true,
    onSelectResult,
    resultInfo,
  );

  useEffect(() => {
    mapRef.current?.on('click', onMapClick);
    mapRef.current?.on('pointermove', onPointerMoveSlider);
    return () => {
      mapRef.current?.un('click', onMapClick);
      mapRef.current?.un('pointermove', onPointerMoveSlider);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapRef.current, onMapClick]);

  // eslint-disable-next-line no-unused-vars
  const setCompareImage = obs => evt => {
    setCompare(obs);
  };

  useEffect(() => {
    setCurrent(observations.find(observation => observation.layer === layer));
  }, [layer, observations]);

  useEffect(() => {
    if (bounds) {
      const extent = transformExtent(bounds, 'EPSG:3857', 'EPSG:4326');
      setView({extent});
      setCompare(null);
    }
  }, [bounds]);

  const handleSliderChange = useCallback(newValue => {
    sliderValueRef.current = newValue;
    window.setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.render();
      }
    });
  }, []);

  const onPrecompose = useCallback(
    event => {
      if (!compare) {
        return;
      }
      const ctx = event.context;
      ctx.restore();
      const sliderValue = sliderValueRef.current;
      const mapSize = mapRef.current.getSize();
      const width = mapSize[0] * (sliderValue / 100);
      const tl = getRenderPixel(event, [width, 0]);
      const tr = getRenderPixel(event, [mapSize[0], 0]);
      const bl = getRenderPixel(event, [width, mapSize[1]]);
      const br = getRenderPixel(event, mapSize);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tl[0], tl[1]);
      ctx.lineTo(bl[0], bl[1]);
      ctx.lineTo(br[0], br[1]);
      ctx.lineTo(tr[0], tr[1]);
      ctx.closePath();
      ctx.clip();
    },
    [compare],
  );

  const onPostcompose = useCallback(event => {
    const ctx = event.context;
    ctx.restore();
  }, []);

  const selectedStyle = useMemo(() => getDrawStyle('#001F23'), []);

  return (
    <ThemeProvider dark>
      <Modal open={open}>
        <Drawer>
          <Box padding={3}>
            <Typography variant="h5">View change over time</Typography>
          </Box>
          <Box overflow="auto" margin={2} flex={1}>
            {view && (
              <ObservationsGrid
                observations={observations}
                current={current}
                compare={compare}
                detectionsURL={detectionsURL}
                interval={interval}
                view={view}
                featureStyle={featureStyle}
                onChange={observation => setCompareImage(observation)}
                mosaicsInfo={mosaicsInfo}
              />
            )}
          </Box>
        </Drawer>
        <RightFrame>
          <Grid container>
            <Grid item xs={6}>
              <Box padding={1}>
                <ToggleButton
                  title="Side by side compare"
                  onClick={() =>
                    onCompareMethodChange(COMPARE_METHODS.SIDE_BY_SIDE)
                  }
                  selected={compareMethod === COMPARE_METHODS.SIDE_BY_SIDE}
                >
                  <SvgIcon>
                    <SideBySideIcon />
                  </SvgIcon>
                </ToggleButton>
                <ToggleButton
                  title="Slider compare"
                  onClick={() => onCompareMethodChange(COMPARE_METHODS.SLIDER)}
                  selected={compareMethod === COMPARE_METHODS.SLIDER}
                >
                  <SvgIcon>
                    <SlideIcon />
                  </SvgIcon>
                </ToggleButton>
              </Box>
            </Grid>
            <Grid item xs={6} align="right">
              <ToolbarButton onClick={() => onClose()}>Exit</ToolbarButton>
            </Grid>
          </Grid>
          <MapParent>
            <AutoSizer>
              {({height, width}) => (
                <div
                  style={{
                    width,
                    height,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                  }}
                >
                  <SizerCallback
                    width={width}
                    height={height}
                    onResize={() => {
                      if (mapRef.current) {
                        mapRef.current.updateSize();
                      }
                    }}
                  />

                  {compareMethod === COMPARE_METHODS.SIDE_BY_SIDE && (
                    <SideBySide
                      compare={compare}
                      detectionsURL={detectionsURL}
                      featureStyle={featureStyle}
                      imageryURL={imageryURL}
                      onSelectResult={onSelectResult}
                      resultInfo={resultInfo}
                      view={view}
                      setView={setView}
                      mosaicsInfo={mosaicsInfo}
                    />
                  )}
                  {compareMethod === COMPARE_METHODS.SLIDER && (
                    <Map
                      innerRef={node => {
                        mapRef.current = node;
                      }}
                      style={{
                        cursor: hovering ? 'pointer' : 'default',
                        width: '100%',
                        height: '100%',
                      }}
                      animation={{duration: 300}}
                      view={view}
                      onViewChange={setView}
                    >
                      {!!compare && (
                        <TileLayer
                          url={getImageryURL(
                            `Mosaic:${
                              getMosaicsByDate(
                                mosaicsInfo,
                                new Date(compare.observed),
                              )[0].name
                            }`,
                            auth.user?.accessToken,
                          )}
                          data-id={compare.observed}
                        />
                      )}
                      {!!compare && (
                        <TileLayer
                          onPreRender={onPrecompose}
                          onPostRender={onPostcompose}
                          url={imageryURL}
                          data-id={`${compare.observed}-imagery`}
                        />
                      )}
                      {!compare && (
                        <TileLayer
                          data-id={`${imageryURL}-compare`}
                          url={imageryURL}
                        />
                      )}
                      {detectionsURL && (
                        <VectorTileLayer
                          layerStyle={featureStyle}
                          url={detectionsURL}
                        />
                      )}
                      {!!extent && (
                        <VectorLayer
                          layerStyle={selectedStyle}
                          data={{
                            type: 'Feature',
                            geometry: {
                              type: 'Polygon',
                              coordinates: fromExtent(extent).getCoordinates(),
                            },
                          }}
                        />
                      )}
                    </Map>
                  )}
                  {compareMethod === COMPARE_METHODS.SLIDER &&
                    !!mapRef.current &&
                    !!compare?.observed && (
                      <CompareSlider
                        initialValue={initial}
                        mapWidth={width}
                        onChange={handleSliderChange}
                      />
                    )}
                  <RightCardBox>
                    <StyledCard>
                      <CardHeader
                        title={
                          <CardHeaderTitle>
                            <LockOutlinedIcon sx={{marginRight: 2}} />
                            <Typography variant="h6">
                              {current?.observed
                                ? toLocaleDateString(
                                    new Date(current?.observed),
                                    interval >= 1,
                                  )
                                : ''}
                            </Typography>
                          </CardHeaderTitle>
                        }
                      />
                    </StyledCard>
                  </RightCardBox>
                  {!compare?.observed && (
                    <ChooseDateBox>
                      <ChooseDateTypography variant="h5">
                        Choose date below to compare
                      </ChooseDateTypography>
                    </ChooseDateBox>
                  )}
                  {!!compare?.observed && (
                    <LeftCardBox>
                      <StyledCard>
                        <CardHeader
                          title={toLocaleDateString(
                            new Date(compare?.observed),
                            interval >= 1,
                          )}
                          titleTypographyProps={{variant: 'h6'}}
                        />
                      </StyledCard>
                    </LeftCardBox>
                  )}
                </div>
              )}
            </AutoSizer>
          </MapParent>
        </RightFrame>
      </Modal>
    </ThemeProvider>
  );
};

ChangeViewer.propTypes = {
  layer: string,
  bounds: array,
  detectionsURL: string,
  featureStyle: func,
  imageryURL: string,
  observations: array,
  onClose: func.isRequired,
  open: bool.isRequired,
  onSelectResult: func,
  resultInfo: object,
  mosaicsInfo: array,
};

export default ChangeViewer;
